import {
  Box,
  FormControl,
  FormLabel,
  Select,
  VStack,
  HStack,
  Input,
  Spacer,
  Image,
  Divider,
  Heading,
} from '@chakra-ui/react'
import { useCallback, useContext, useEffect, useState } from 'react'
import { IModel } from '../../../type/modelType'
import { IMesh } from '../../../type/modelType'
import {
  getMeshGroups,
  mergeMeshesSettings,
  readGltf,
  saveModelConfig,
  switchToConfigurationMode,
  switchToDesignMode,
} from '../../../utils/modelEngine/configuration'
import { useDropzone } from 'react-dropzone'
import { uploadFileToStorage } from '../../../utils/modelEngine/uploadFile'
import MeshConfig from './meshConfig'
import { AppContext } from '../../../libs/context'
import {
  getProductDesignIdFromURL,
  loadDesignState,
  saveDesignState,
} from '../../../utils/design/saveLoad'

const ModelConfiguration = () => {
  const context = useContext(AppContext)
  const { shop, model, setModel } = context

  const designId = getProductDesignIdFromURL() as string

  const [modelConfig, setModelConfig] = useState<IModel>(model.current)

  const [meshGroups, setMeshGroups] = useState<IMesh[]>(model.current.meshes)
  const [meshGroup, setMeshGroup] = useState<IMesh | null>(null)

  const saveConfig = (config: IModel) => {
    saveModelConfig(config, context)
    setModelConfig(config)
  }

  const buildDimensions = (
    currentConfig: IModel,
    dimensions: { [key: string]: string | number }
  ): IModel => {
    return {
      ...currentConfig,
      dimensions: {
        unit: currentConfig.dimensions?.unit ?? 'CM',
        w: currentConfig.dimensions?.w ?? 0,
        h: currentConfig.dimensions?.h ?? 0,
        d: currentConfig.dimensions?.d ?? 0,
        ...dimensions,
      },
    }
  }

  const onPreviewDrop = useCallback(acceptedFiles => {
    if (acceptedFiles[0]) {
      uploadFileToStorage(acceptedFiles[0], modelConfig.id).then(preview => {
        saveConfig({
          ...modelConfig,
          preview,
        })
      })
    }
  }, [])

  const {
    getRootProps: getRootPreviewProps,
    getInputProps: getInputPreviewProps,
  } = useDropzone({
    onDrop: onPreviewDrop,
    accept: '.jpg,.jpeg,.png',
  })

  useEffect(() => {
    readGltf(modelConfig.source).then(gltf => {
      const meshGroupFromFile = getMeshGroups(gltf, modelConfig.meshes)
      const mergedMeshGroups = mergeMeshesSettings(
        modelConfig.meshes,
        meshGroupFromFile
      )
      setMeshGroups(mergedMeshGroups)
      setModelConfig({
        ...modelConfig,
        meshes: mergedMeshGroups,
      })
    })
  }, [])

  useEffect(() => {
    if (meshGroup) {
      const newModel = {
        ...modelConfig,
        meshes: [
          ...modelConfig.meshes.filter(m => m.id !== meshGroup.id),
          meshGroup,
        ],
      }

      setModelConfig(newModel)
      switchToConfigurationMode(newModel)
      setMeshGroups([
        ...meshGroups.filter(g => g.id !== meshGroup.id),
        meshGroup,
      ])
    }
  }, [meshGroup])

  useEffect(() => {
    if (modelConfig) {
      saveModelConfig(modelConfig, context)
      setModel(modelConfig)
    }
  }, [modelConfig])

  useEffect(() => {
    switchToConfigurationMode(model.current)
    return () => {
      context.fetchShop()
      switchToDesignMode(model.current.id, designId, context)
    }
  }, [])

  return (
    <VStack spacing={4} align="stretch" paddingInline="30px">
      <Heading as="h3" size="sm">
        Information
      </Heading>
      <FormControl id="title" isRequired>
        <FormLabel>Title</FormLabel>
        <Input
          placeholder="Title"
          defaultValue={modelConfig.title}
          onBlur={e => {
            const newConfig = {
              ...modelConfig,
              title: e.target.value,
            }
            saveConfig(newConfig)
          }}
        />
      </FormControl>
      <Spacer />
      <FormControl id="dimensions" isRequired>
        <FormLabel>Dimensions</FormLabel>
        <VStack spacing={1}>
          <Select
            placeholder="Unit"
            defaultValue={modelConfig.dimensions?.unit ?? 'CM'}
            onBlur={e => {
              saveConfig(buildDimensions(modelConfig, { unit: e.target.value }))
            }}
          >
            <option value="CM">CM</option>
            <option value="IN">IN</option>
          </Select>
          <Spacer />
          <HStack spacing={1}>
            <Input
              placeholder="w"
              defaultValue={modelConfig.dimensions?.w ?? 0}
              onBlur={e => {
                saveConfig(
                  buildDimensions(modelConfig, {
                    w: parseFloat(e.target.value),
                  })
                )
              }}
            />
            <Spacer />
            <Input
              placeholder="h"
              defaultValue={modelConfig.dimensions?.h ?? 0}
              onBlur={e => {
                saveConfig(
                  buildDimensions(modelConfig, {
                    h: parseFloat(e.target.value),
                  })
                )
              }}
            />
            <Spacer />
            <Input
              placeholder="d"
              defaultValue={modelConfig.dimensions?.d ?? 0}
              onBlur={e => {
                saveConfig(
                  buildDimensions(modelConfig, {
                    d: parseFloat(e.target.value),
                  })
                )
              }}
            />
          </HStack>
        </VStack>
      </FormControl>
      <Spacer />
      <FormControl id="preview" isRequired>
        <FormLabel>Preview</FormLabel>
        <Box {...getRootPreviewProps()} cursor="pointer">
          {modelConfig.preview && modelConfig.preview !== '' ? (
            <Image
              boxSize="100px"
              objectFit="cover"
              src={modelConfig.preview}
              alt="preview image"
            />
          ) : (
            <Image
              boxSize="100px"
              objectFit="cover"
              src="/design-your-own.jpg"
              alt="preview image"
            />
          )}

          {/* <Button colorScheme="brand">Upload</Button> */}
          <input {...getInputPreviewProps()} />
        </Box>
      </FormControl>
      <Spacer />
      <Divider />
      <Spacer />
      <Heading as="h3" size="sm">
        Design
      </Heading>
      <FormControl id="mesh">
        <FormLabel>Select a Zone</FormLabel>
        <Select
          placeholder="Please Select"
          onChange={e => {
            setMeshGroup(meshGroups.find(m => m.id === e.target.value) as IMesh)
          }}
        >
          {meshGroups
            .sort((a, b) => (a.title < b.title ? -1 : 1))
            .map(meshGroup => (
              <option
                key={`meshgroup-option-${meshGroup.id}`}
                value={meshGroup.id}
              >
                {meshGroup.title}
              </option>
            ))}
        </Select>
      </FormControl>

      {meshGroup && (
        <MeshConfig mesh={meshGroup} setMesh={setMeshGroup} shop={shop} />
      )}
    </VStack>
  )
}

export default ModelConfiguration
